/** Range Overview (Demo Mode only) - the Range Planning "control tower" for food ranges across
 * customers, contracts, site groups and sites. Answers "what food range is live, planned,
 * changing, or at risk?" via 8 dashboard tiles and a tabbed layout (Ranges / Supplier
 * performance & rebate visibility / Trial outcomes / AI recipe checks), all computed live from
 * the same Menus/RecipeV2/Item Rationalisation/Attributes & Allergens mock data the rest of
 * Range Planning uses (src/app/services/api/rangeOverview). Also keeps the former
 * ProcurementRollup screen's supplier governance and trial review content as tabs on the new
 * control tower (that page is retired - see App.tsx/useDrawer.tsx) since nothing in the new
 * spec conflicts with keeping that capability. */

import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  SvgIcon,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Co2,
  CompareArrows,
  ContentCopy,
  EventOutlined,
  Inventory2,
  MenuBook,
  PendingActions,
  PublishOutlined,
  Storefront,
  TrendingUp,
  WarningAmber,
} from '@mui/icons-material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';
import { Plus } from '../../assets';
import { UserContext } from '../../components/shared/useUser';
import { supplierGovernanceProfiles } from '../../mocks/data/supplierGovernanceData';
import { productPlanningProfiles } from '../../mocks/data/productPlanningData';
import { menus } from '../../mocks/data/menusData';
import { attributesAllergensClient } from '../../app/services/api/attributesAllergens';
import { rangeOverviewClient } from '../../app/services/api/rangeOverview';
import { Range, RangeScopeType, RangeStatus, RangeTile, ReadinessStatus } from '../../app/services/api/rangeOverview/rangeOverviewTypes';
import AIChecksPanel from '../../components/shared/aiChecks/AIChecksPanel';
import { aiChecksClient } from '../../app/services/api/aiChecks/aiChecksEngine';
import { AICheckFinding } from '../../app/services/api/aiChecks/aiChecksTypes';

const RANGE_STATUS_COLOR: Record<RangeStatus, 'success' | 'warning' | 'info' | 'default' | 'error'> = {
  [RangeStatus.Draft]: 'default',
  [RangeStatus.InReview]: 'info',
  [RangeStatus.Approved]: 'info',
  [RangeStatus.Scheduled]: 'warning',
  [RangeStatus.Live]: 'success',
  [RangeStatus.Expired]: 'default',
  [RangeStatus.Archived]: 'default',
  [RangeStatus.Blocked]: 'error',
};

const READINESS_COLOR: Record<ReadinessStatus, 'success' | 'warning' | 'error'> = {
  [ReadinessStatus.Ready]: 'success',
  [ReadinessStatus.Attention]: 'warning',
  [ReadinessStatus.Blocked]: 'error',
};

// Visual treatment (icon/accent colour/subtitle) for each of the 8 dashboard tiles, keyed by
// RangeTile.key - the tile values themselves are computed live by rangeOverviewClientMocked.
const TILE_VISUALS: Record<string, { icon: JSX.Element; color: string; subtitleKey: string; defaultSubtitle: string }> = {
  activeMenus: { icon: <MenuBook />, color: '#1976d2', subtitleKey: 'tiles.subtitle.activeMenus', defaultSubtitle: 'Live across the estate' },
  menusAwaitingApproval: { icon: <PendingActions />, color: '#ed6c02', subtitleKey: 'tiles.subtitle.menusAwaitingApproval', defaultSubtitle: 'Needs review before going live' },
  recipesWithCostMovement: { icon: <TrendingUp />, color: '#9c27b0', subtitleKey: 'tiles.subtitle.recipesWithCostMovement', defaultSubtitle: 'Cost drifted from contracted price' },
  recipesWithAllergenWarnings: { icon: <WarningAmber />, color: '#d32f2f', subtitleKey: 'tiles.subtitle.recipesWithAllergenWarnings', defaultSubtitle: 'May-contain flags to confirm' },
  itemsRequiringRationalisation: { icon: <Inventory2 />, color: '#2e7d32', subtitleKey: 'tiles.subtitle.itemsRequiringRationalisation', defaultSubtitle: 'Duplicates, unused or missing data' },
  menusMissingCarbonData: { icon: <Co2 />, color: '#0891b2', subtitleKey: 'tiles.subtitle.menusMissingCarbonData', defaultSubtitle: 'Recipes without a carbon score' },
  sitesWithoutPublishedRange: { icon: <Storefront />, color: '#d32f2f', subtitleKey: 'tiles.subtitle.sitesWithoutPublishedRange', defaultSubtitle: 'No range has gone live yet' },
  upcomingMenuChanges: { icon: <EventOutlined />, color: '#1976d2', subtitleKey: 'tiles.subtitle.upcomingMenuChanges', defaultSubtitle: 'Scheduled or awaiting review' },
};

// lazy: client-side pagination only - rangeOverviewClientMocked has no server-side paging params.
const RANGES_PAGE_SIZE = 10;
const SUPPLIERS_PAGE_SIZE = 10;

const SCOPE_TYPES: RangeScopeType[] = ['WholeEstate', 'Site', 'MultiSite', 'ClientAccount', 'SiteGroup'];

const ReadinessChip = ({ status, note }: { status: ReadinessStatus; note: string }) => (
  <Tooltip title={note}>
    <Chip label={status}
      color={READINESS_COLOR[status]}
      size="small"
      variant="outlined" />
  </Tooltip>
);

const RangeOverviewPage = () => {
  const { t } = useTranslation('rangeOverview');
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermissionTo } = useContext(UserContext);
  const canApproveRollout = hasPermissionTo(['ProcurementRolloutApprove']);
  const canPublishRange = hasPermissionTo(['ProcurementRolloutApprove']);

  const [scopeType, setScopeType] = useState<RangeScopeType>('WholeEstate');
  const [scopeValue, setScopeValue] = useState<string | string[]>('');
  const [search, setSearch] = useState('');
  const [rangeRows, setRangeRows] = useState<Range[]>([]);
  const [tiles, setTiles] = useState<RangeTile[]>([]);
  const [scopeOptions, setScopeOptions] = useState<{ sites: string[]; clientAccounts: string[]; siteGroups: string[] }>({ sites: [], clientAccounts: [], siteGroups: [] });
  const [compareRange, setCompareRange] = useState<Range | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [trialApproved, setTrialApproved] = useState(false);
  const [conflictCount, setConflictCount] = useState<number | null>(null);
  const [aiChecksFindings, setAiChecksFindings] = useState<AICheckFinding[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [rangesPage, setRangesPage] = useState(0);
  const [suppliersPage, setSuppliersPage] = useState(0);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newRange, setNewRange] = useState({ name: '', customerName: '', clientAccount: '', siteGroup: '', siteNames: [] as string[] });

  useEffect(() => {
    attributesAllergensClient.getConflicts().then((conflicts) => setConflictCount(conflicts.length));
    rangeOverviewClient.getScopeOptions().then(setScopeOptions);
    aiChecksClient.getEstateFindings().then(setAiChecksFindings);
  }, []);

  const refreshTiles = useCallback(() => {
    rangeOverviewClient.getTiles().then(setTiles);
  }, []);

  const loadRanges = useCallback(() => {
    rangeOverviewClient.getRanges(scopeType, scopeValue, search).then((res) => {
      setRangeRows(res.data);
      setRangesPage(0);
    });
  }, [scopeType, scopeValue, search]);

  useEffect(() => { refreshTiles(); }, [refreshTiles]);
  useEffect(() => { loadRanges(); }, [loadRanges]);

  const handleScopeTypeChange = (value: RangeScopeType) => {
    setScopeType(value);
    setScopeValue(value === 'MultiSite' ? [] : '');
  };

  const handleDuplicate = async (range: Range) => {
    await rangeOverviewClient.duplicateRange(range.id);
    loadRanges();
  };

  const handlePublish = async (range: Range) => {
    setPublishError(null);
    try {
      await rangeOverviewClient.publishRange(range.id);
      loadRanges();
      refreshTiles();
    } catch (err) {
      setPublishError(err instanceof Error ? err.message : t('publishFailed', 'Unable to publish range.'));
    }
  };

  const handleCreate = async () => {
    if (!newRange.name.trim()) return;
    await rangeOverviewClient.createRange(newRange);
    setShowCreateDialog(false);
    setNewRange({ name: '', customerName: '', clientAccount: '', siteGroup: '', siteNames: [] });
    loadRanges();
    refreshTiles();
  };

  const linkedMenu = compareRange?.menuId ? menus.find((m) => m.id === compareRange.menuId) : undefined;

  const suppliers = Object.values(supplierGovernanceProfiles);
  const trialProfile = Object.values(productPlanningProfiles)[0];

  const rangesMaxPages = Math.max(1, Math.ceil(rangeRows.length / RANGES_PAGE_SIZE));
  const paginatedRanges = rangeRows.slice(rangesPage * RANGES_PAGE_SIZE, (rangesPage + 1) * RANGES_PAGE_SIZE);
  const suppliersMaxPages = Math.max(1, Math.ceil(suppliers.length / SUPPLIERS_PAGE_SIZE));
  const paginatedSuppliers = suppliers.slice(suppliersPage * SUPPLIERS_PAGE_SIZE, (suppliersPage + 1) * SUPPLIERS_PAGE_SIZE);

  return (
    <PageArea>
      <Stack spacing={6}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <PageTitle>{t('title', 'Range Overview')}</PageTitle>
            <Typography variant="textSM"
              color="text.secondary"
              sx={{ mt: 1, maxWidth: 640 }}>
              {t('intro', 'What food range is live, planned, changing, or at risk - across customers, contracts, site groups and sites.')}
            </Typography>
          </Box>
          <Button
            onClick={() => setShowCreateDialog(true)}
            size="lg"
            startIcon={<SvgIcon><Plus /></SvgIcon>}
          >
            {t('createRange', 'Create Range')}
          </Button>
        </Box>

        {/* 8 dashboard tiles */}
        <Grid container
          spacing={4}>
          {tiles.map((tile) => {
            const visual = TILE_VISUALS[tile.key];
            return (
              <Grid item
                key={tile.key}
                xs={6}
                md={3}>
                <Section style={{ p: 4, mb: 0, cursor: tile.href !== '#' ? 'pointer' : 'default' }}
                  onClick={() => tile.href !== '#' && navigate(tile.href)}>
                  <Box display="flex"
                    alignItems="center"
                    justifyContent="space-between">
                    <Box flex={1}>
                      <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                        {t(tile.labelKey, tile.defaultLabel)}
                      </Box>
                      <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                        {tile.value}
                      </Box>
                      <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                        {visual ? t(visual.subtitleKey, visual.defaultSubtitle) : ''}
                      </Box>
                    </Box>
                    <Box sx={{ backgroundColor: `${visual?.color ?? '#1976d2'}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: visual?.color ?? '#1976d2' }}>
                      <SvgIcon fontSize="medium">{visual?.icon}</SvgIcon>
                    </Box>
                  </Box>
                </Section>
              </Grid>
            );
          })}
        </Grid>

        {publishError && (
          <Alert severity="error"
            onClose={() => setPublishError(null)}>{publishError}</Alert>
        )}

        <Tabs
          value={tabValue}
          onChange={(_event, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label={t('tabs.ranges', 'Ranges')} />
          <Tab label={t('tabs.supplierPerformance', 'Supplier Performance & Rebate Visibility')} />
          <Tab label={t('tabs.trialOutcomes', 'Trial Outcomes')} />
          <Tab label={t('tabs.aiChecks', 'AI Recipe Checks')} />
        </Tabs>

        {/* Ranges tab */}
        <Box sx={{ display: tabValue === 0 ? 'block' : 'none' }}>
          <Stack spacing={4}>
            {/* Estate scope selector - one site / multiple sites / client account / site group /
                whole estate - sits directly on the page background, not in a bordered panel. */}
            <Grid container
              spacing={3}
              alignItems="center">
              <Grid item
                md={3}
                xs={12}>
                <TextField
                  select
                  fullWidth
                  label={t('scope.label', 'Scope')}
                  value={scopeType}
                  onChange={(e) => handleScopeTypeChange(e.target.value as RangeScopeType)}
                >
                  {SCOPE_TYPES.map((s) => (
                    <MenuItem key={s}
                      value={s}>{t(`scope.${s}`, s)}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              {scopeType === 'Site' && (
                <Grid item
                  md={3}
                  xs={12}>
                  <Autocomplete
                    options={scopeOptions.sites}
                    value={(scopeValue as string) || null}
                    onChange={(_e, v) => setScopeValue(v ?? '')}
                    renderInput={(params) => <TextField {...params}
                      label={t('scope.Site', 'Site')} />}
                  />
                </Grid>
              )}
              {scopeType === 'MultiSite' && (
                <Grid item
                  md={4}
                  xs={12}>
                  <Autocomplete
                    multiple
                    options={scopeOptions.sites}
                    value={scopeValue as string[]}
                    onChange={(_e, v) => setScopeValue(v)}
                    renderInput={(params) => <TextField {...params}
                      label={t('scope.MultiSite', 'Multiple sites')} />}
                  />
                </Grid>
              )}
              {scopeType === 'ClientAccount' && (
                <Grid item
                  md={3}
                  xs={12}>
                  <Autocomplete
                    options={scopeOptions.clientAccounts}
                    value={(scopeValue as string) || null}
                    onChange={(_e, v) => setScopeValue(v ?? '')}
                    renderInput={(params) => <TextField {...params}
                      label={t('scope.ClientAccount', 'Client account')} />}
                  />
                </Grid>
              )}
              {scopeType === 'SiteGroup' && (
                <Grid item
                  md={3}
                  xs={12}>
                  <Autocomplete
                    options={scopeOptions.siteGroups}
                    value={(scopeValue as string) || null}
                    onChange={(_e, v) => setScopeValue(v ?? '')}
                    renderInput={(params) => <TextField {...params}
                      label={t('scope.SiteGroup', 'Site group')} />}
                  />
                </Grid>
              )}
              <Grid item
                md={3}
                xs={12}
                sx={{ ml: 'auto' }}>
                <TextField
                  fullWidth
                  label={t('search', 'Search')}
                  placeholder={t('searchPlaceholder', 'Search range, customer or client account')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Grid>
            </Grid>

            <Section>
              <TableHeader>
                {t('mainGridTitle', 'Ranges')}
                <Pill text={t('rangesPill', '{{count}} Ranges', { count: rangeRows.length })} />
              </TableHeader>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('col.rangeName', 'Range name')}</TableCell>
                      <TableCell>{t('col.customer', 'Customer / Client')}</TableCell>
                      <TableCell>{t('col.siteGroup', 'Site group')}</TableCell>
                      <TableCell>{t('col.sites', 'Sites')}</TableCell>
                      <TableCell>{t('col.servicePeriod', 'Service period')}</TableCell>
                      <TableCell>{t('col.activeDates', 'Active dates')}</TableCell>
                      <TableCell>{t('col.status', 'Status')}</TableCell>
                      <TableCell>{t('col.costStatus', 'Cost status')}</TableCell>
                      <TableCell>{t('col.allergenStatus', 'Allergen status')}</TableCell>
                      <TableCell>{t('col.carbonStatus', 'Carbon status')}</TableCell>
                      <TableCell>{t('col.lastPublished', 'Last published')}</TableCell>
                      <TableCell align="right">{t('col.actions', 'Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rangeRows.length === 0 && (
                      <TableRow><TableCell colSpan={12}>{t('noRanges', 'No ranges match this scope.')}</TableCell></TableRow>
                    )}
                    {paginatedRanges.map((range) => (
                      <TableRow key={range.id}
                        hover>
                        <TableCell>{range.name}</TableCell>
                        <TableCell>
                          <Typography variant="textSM">{range.customerName}</Typography>
                          <Typography variant="textXS"
                            color="text.secondary">{range.clientAccount}</Typography>
                        </TableCell>
                        <TableCell>{range.siteGroup}</TableCell>
                        <TableCell>{range.sites.length}</TableCell>
                        <TableCell>{range.servicePeriod}</TableCell>
                        <TableCell>{range.activeFrom}{range.activeTo ? ` \u2013 ${range.activeTo}` : ''}</TableCell>
                        <TableCell>
                          <Chip label={t(`status.${range.status}`, range.status)}
                            color={RANGE_STATUS_COLOR[range.status]}
                            size="small" />
                        </TableCell>
                        <TableCell><ReadinessChip status={range.costStatus}
                          note={range.costStatusNote} /></TableCell>
                        <TableCell><ReadinessChip status={range.allergenStatus}
                          note={range.allergenStatusNote} /></TableCell>
                        <TableCell><ReadinessChip status={range.carbonStatus}
                          note={range.carbonStatusNote} /></TableCell>
                        <TableCell>{range.lastPublished ?? '-'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('action.compare', 'Compare planned vs live')}>
                            <IconButton size="small"
                              onClick={() => setCompareRange(range)}>
                              <CompareArrows fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('action.copy', 'Copy range to another site')}>
                            <IconButton size="small"
                              onClick={() => handleDuplicate(range)}>
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canPublishRange && range.status !== RangeStatus.Live && range.status !== RangeStatus.Archived && range.status !== RangeStatus.Expired && (
                            <Tooltip title={t('action.publish', 'Publish to selected sites')}>
                              <IconButton size="small"
                                onClick={() => handlePublish(range)}>
                                <PublishOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <CustomPagination useNoDataMessage={false}
                page={rangesPage}
                setPage={setRangesPage}
                maxPages={rangesMaxPages}
                breakpointForChangeDisplay={120000} />
            </Section>
          </Stack>
        </Box>

        {/* Supplier performance & contract/rebate visibility tab */}
        <Box sx={{ display: tabValue === 1 ? 'block' : 'none' }}>
          <Section>
            <TableHeader>
              {t('supplierPerformance', 'Supplier performance & contract/rebate visibility')}
              <Pill text={t('suppliersPill', '{{count}} Suppliers', { count: suppliers.length })} />
            </TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('supplier', 'Supplier')}</TableCell>
                    <TableCell>{t('approvalStatus', 'Approval status')}</TableCell>
                    <TableCell>{t('onTimeDelivery', 'On-time delivery')}</TableCell>
                    <TableCell>{t('qualityScore', 'Quality score')}</TableCell>
                    <TableCell>{t('contractRenewal', 'Contract renewal')}</TableCell>
                    <TableCell>{t('rebateYtd', 'Rebate YTD')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSuppliers.map((supplier) => (
                    <TableRow key={supplier.supplierId}>
                      <TableCell>{t('supplierNumber', 'Supplier #{{id}}', { id: supplier.supplierId })}</TableCell>
                      <TableCell><Chip label={supplier.approvalStatus}
                        size="small" /></TableCell>
                      <TableCell>{supplier.performance ? `${supplier.performance.onTimeDeliveryPercentage}%` : '-'}</TableCell>
                      <TableCell>{supplier.performance ? `${supplier.performance.qualityScore} / 5` : '-'}</TableCell>
                      <TableCell>{supplier.contract?.renewalStatus ?? '-'}</TableCell>
                      <TableCell>{supplier.rebateAgreements.reduce((s, r) => s + r.accruedYtd, 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination useNoDataMessage={false}
              page={suppliersPage}
              setPage={setSuppliersPage}
              maxPages={suppliersMaxPages}
              breakpointForChangeDisplay={120000} />
          </Section>
        </Box>

        {/* Trial outcomes tab - padding increased (p:6) and structured to match other read-only
            detail sections elsewhere in the app (e.g. Section p:5/p:6 field grids). */}
        <Box sx={{ display: tabValue === 2 ? 'block' : 'none' }}>
          <Section style={{ p: 6 }}>
            {trialProfile ? (
              <>
                <Stack direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  sx={{ mb: 4 }}>
                  <Typography variant="textSM"
                    fontWeight={600}>
                    {t('trialReview', 'Trial review: {{name}}', { name: trialProfile.productName })}
                  </Typography>
                  <Chip label={trialApproved ? t('approvedPermanent', 'Approved permanent') : trialProfile.trial.outcome}
                    color={trialApproved ? 'success' : 'default'}
                    size="small" />
                </Stack>
                <Grid container
                  spacing={4}
                  sx={{ mb: 4 }}>
                  <Grid item
                    xs={6}
                    md={3}>
                    <Typography variant="textXS"
                      color="text.secondary">{t('forecastUnits', 'Forecast units/week')}</Typography>
                    <Typography variant="textSM">{trialProfile.expectedDemand.unitsPerWeek}</Typography>
                  </Grid>
                  <Grid item
                    xs={6}
                    md={3}>
                    <Typography variant="textXS"
                      color="text.secondary">{t('forecastMargin', 'Forecast margin')}</Typography>
                    <Typography variant="textSM">{trialProfile.forecastPnL.marginPercentage.toFixed(1)}%</Typography>
                  </Grid>
                  <Grid item
                    xs={6}
                    md={3}>
                    <Typography variant="textXS"
                      color="text.secondary">{t('supplier', 'Supplier')}</Typography>
                    <Typography variant="textSM">{trialProfile.proposedSupplierName ?? '-'}</Typography>
                  </Grid>
                  <Grid item
                    xs={6}
                    md={3}>
                    <Typography variant="textXS"
                      color="text.secondary">{t('trialWindow', 'Trial window')}</Typography>
                    <Typography variant="textSM">{trialProfile.trial.startDate} - {trialProfile.trial.endDate}</Typography>
                  </Grid>
                </Grid>
                <Typography variant="textXS"
                  color="text.secondary"
                  sx={{ mb: 1 }}>{t('successCriteria', 'Success criteria')}</Typography>
                <Stack spacing={0.5}
                  sx={{ mb: 4 }}>
                  {trialProfile.trial.successCriteria.map((criterion) => (
                    <Typography key={criterion}
                      variant="textSM">{'\u2022'} {criterion}</Typography>
                  ))}
                </Stack>
                {!trialApproved && canApproveRollout && (
                  <Button variant="primary"
                    onClick={() => setTrialApproved(true)}>
                    {t('approveRollout', 'Approve as permanent range item (Operations)')}
                  </Button>
                )}
                {!trialApproved && !canApproveRollout && (
                  <Typography variant="textXS"
                    color="text.secondary">
                    {t('requiresOperationsRole', 'Requires the Operations demo role to approve.')}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="textSM"
                color="text.secondary">
                {t('noTrial', 'No trial is currently in review.')}
              </Typography>
            )}
          </Section>
        </Box>

        {/* AI recipe checks tab */}
        <Box sx={{ display: tabValue === 3 ? 'block' : 'none' }}>
          <AIChecksPanel findings={aiChecksFindings.slice(0, 10)}
            defaultOpen
            title={t('aiChecksTitle', 'AI Recipe Checks - estate summary')} />
        </Box>
      </Stack>

      <Dialog open={!!compareRange}
        onClose={() => setCompareRange(null)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('compareTitle', 'Compare planned vs live: {{name}}', { name: compareRange?.name ?? '' })}</DialogTitle>
        <DialogContent>
          {linkedMenu ? (
            <Stack spacing={2}
              sx={{ mt: 1 }}>
              <Typography variant="textSM"
                color="text.secondary">
                {t('compareIntro', 'Version history for the linked menu - the most recent entry is the planned/draft state, earlier entries are what was previously live.')}
              </Typography>
              {[...linkedMenu.versionHistory].reverse().map((v, idx) => (
                <Box key={v.version}
                  sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Stack direction="row"
                    justifyContent="space-between">
                    <Typography variant="textSM"
                      fontWeight={600}>{t('version', 'Version {{v}}', { v: v.version })}</Typography>
                    <Chip label={idx === 0 ? t('planned', 'Planned') : t('live', 'Live')}
                      color={idx === 0 ? 'warning' : 'success'}
                      size="small" />
                  </Stack>
                  <Typography variant="textXS"
                    color="text.secondary">{v.date}</Typography>
                  <Typography variant="textSM"
                    sx={{ mt: 1 }}>{v.changeSummary}</Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="textSM"
              color="text.secondary"
              sx={{ mt: 1 }}>
              {t('noLinkedMenu', 'No linked menu version history yet for this range.')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={() => setCompareRange(null)}>{t('close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('createRange', 'Create Range')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 2 }}>
            <TextField
              label={t('col.rangeName', 'Range name')}
              value={newRange.name}
              onChange={(e) => setNewRange({ ...newRange, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('customerName', 'Customer')}
              value={newRange.customerName}
              onChange={(e) => setNewRange({ ...newRange, customerName: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('clientAccount', 'Client account')}
              value={newRange.clientAccount}
              onChange={(e) => setNewRange({ ...newRange, clientAccount: e.target.value })}
              fullWidth
            />
            <TextField
              label={t('col.siteGroup', 'Site group')}
              value={newRange.siteGroup}
              onChange={(e) => setNewRange({ ...newRange, siteGroup: e.target.value })}
              fullWidth
            />
            <Autocomplete
              multiple
              freeSolo
              options={scopeOptions.sites}
              value={newRange.siteNames}
              onChange={(_e, v) => setNewRange({ ...newRange, siteNames: v as string[] })}
              renderInput={(params) => <TextField {...params}
                label={t('col.sites', 'Sites')}
                placeholder={t('sitesPlaceholder', 'Add one or more sites')} />}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={() => setShowCreateDialog(false)}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button onClick={handleCreate}
            disabled={!newRange.name.trim()}>
            {t('create', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default RangeOverviewPage;
